import { Component, Input, OnInit, TrackByFunction } from '@angular/core'
import { Department } from '@ftr/contracts/api/department'
import { EntityType } from '@ftr/contracts/api/permissions'
import {
  RestrictedModel,
  UserGroup,
  UserGroupPermission,
  UserGroupPermissionId,
  getPermissionDescription,
} from '@ftr/contracts/api/user-group'
import { VocabularyTerms } from '@ftr/contracts/type/core/VocabularyTerms'
import { Uuid } from '@ftr/contracts/type/shared'
import { ApiResult, DestroySubscribers, collect, mapData } from '@ftr/foundation'
import { LaunchDarklyService } from '@ftr/ui-feature-flags'
import { RootLocation } from '@ftr/ui-location'
import { VocabularyTermsService } from '@ftr/ui-vocab'
import { Store } from '@ngxs/store'
import { BehaviorSubject, switchMap } from 'rxjs'
import { UserGroupService } from '../../services'
import { GetCurrentCourtSystemAudioPlaybackFeature, UserState } from '../../store'

export interface Restriction {
  entityType: EntityType
  entityTypeVocabbed: string
  entityNames: string[]
}

export interface Permission {
  id: UserGroupPermissionId
  /**
   * The permission description
   */
  description: string | undefined
  /**
   * Whether the 'Restricted' dropdown is expanded or collapsed
   */
  isExpanded: boolean

  restrictedBy: Restriction[]
}

/**
 * An object containing a map of entity ids to the display name: e.g. {[locationId]: 'A Courthouse - Room 1'}
 */
export type EntityIdWithName = Record<string, string>

export interface RestrictedEntityLabelsByType {
  /**
   * Must match EntityType
   */
  location: EntityIdWithName
  /**
   *
   */
  department: EntityIdWithName
}

type PermissionIdToPermissionMap = { [permissionId in string]: Permission }

@Component({
  selector: 'ftr-user-group-permissions-list',
  templateUrl: './user-group-permissions-list.component.html',
  styleUrls: ['./user-group-permissions-list.component.css'],
})
export class UserGroupPermissionsListComponent extends DestroySubscribers implements OnInit {
  @Input() courtSystemId: Uuid

  @Input() userGroups: UserGroup[]
  @Input() rootLocations: RootLocation[]
  @Input() departments: Department[]

  /**
   * A list of this court's restricted permissions that are enabled e.g. location, department
   */
  @Input() enabledRestrictions: EntityType[]

  refresh = new BehaviorSubject<void>(undefined)

  viewModel: ApiResult<Permission[]>

  constructor(
    private readonly store: Store,
    private readonly vocabularyTermsService: VocabularyTermsService,
    private readonly userGroupService: UserGroupService,
    private readonly launchDarklyService: LaunchDarklyService,
  ) {
    super()
  }

  ngOnInit(): void {
    this.store.dispatch(new GetCurrentCourtSystemAudioPlaybackFeature(this.courtSystemId))
    this.viewModel = this.refresh.pipe(
      switchMap(_ => {
        return ApiResult.combine([
          this.getUserGroupsWithPermissions(),
          this.getRestrictedLabels(),
          this.vocabularyTermsService.observeRemoteTerms(this.courtSystemId),
          this.store.select(UserState.currentCourtSystemAudioPlaybackFeature).pipe(collect(f => f)),
          ApiResult.from(this.launchDarklyService.observeFlagChanges<boolean>('annotations-first-release')),
        ])
      }),
      mapData(([userGroupsWithPermissions, labels, terms, audioPlaybackFeature, annotationsFirstReleaseFlag]) => {
        return this.getPermissionsBasedOnLabels(
          userGroupsWithPermissions,
          terms,
          labels,
          audioPlaybackFeature!.isEnabled,
          audioPlaybackFeature!.configuration?.autoscriptEnabled,
          annotationsFirstReleaseFlag,
        )
      }),
    )
  }

  restrictionIsEnabled(entityType: EntityType): boolean {
    return this.enabledRestrictions.some(t => entityType === t)
  }

  private getPermissionsBasedOnLabels(
    userGroupsWithPermissions: UserGroup[],
    vocabularyTerms: VocabularyTerms,
    restrictedEntityNames: RestrictedEntityLabelsByType | undefined,
    canPlayBackRecordings: boolean,
    isAutoscriptEnabled: boolean | undefined = false,
    hearingAnnotationsPrototypeIsEnabled: boolean = false,
  ): Permission[] {
    if (userGroupsWithPermissions === undefined || userGroupsWithPermissions.length === 0) {
      return []
    }

    const resultMap: PermissionIdToPermissionMap = {}
    const enabledGroups = userGroupsWithPermissions.filter(g => g.isEnabled)

    for (const group of enabledGroups) {
      const enabledPermissions = group.permissions
        .filter(p => p.isEnabled)
        .filter(
          p =>
            hearingAnnotationsPrototypeIsEnabled ||
            (p.id !== UserGroupPermissionId.CaseInformation && p.id !== UserGroupPermissionId.PlaybackHearings),
        )

      const filteredPermissionsByFeature = this.filterPermissionsByFeature(
        enabledPermissions,
        canPlayBackRecordings,
        isAutoscriptEnabled,
      )

      for (const permission of filteredPermissionsByFeature) {
        this.ensureResultMapPermissionEntryExists(resultMap, permission)
        this.addPermissionToResultMap(permission, resultMap, restrictedEntityNames)
      }
    }

    this.deduplicateViewModelResultMap(resultMap)

    // We have to wait for the above object to be fully populated and deduplicated,
    // before we can calculate plurals
    this.populateVocabularyNames(resultMap, vocabularyTerms)

    return Object.values(resultMap)
  }

  private filterPermissionsByFeature(
    permissions: UserGroupPermission[],
    canPlayBackRecordings: boolean,
    isAutoscriptEnabled: boolean,
  ): UserGroupPermission[] {
    return permissions.reduce((acc, permission) => {
      if (
        !canPlayBackRecordings &&
        [UserGroupPermissionId.PlaybackRecordings, UserGroupPermissionId.ReadLogSheets].includes(permission.id)
      ) {
        return acc
      }

      if (
        !isAutoscriptEnabled &&
        [UserGroupPermissionId.ReadStt, UserGroupPermissionId.RequestStt].includes(permission.id)
      ) {
        return acc
      }

      return [...acc, permission]
    }, [] as UserGroupPermission[])
  }

  private populateVocabularyNames(resultMap: PermissionIdToPermissionMap, vocabularyTerms: VocabularyTerms): void {
    for (const v of Object.values(resultMap)) {
      for (const restriction of v.restrictedBy) {
        restriction.entityTypeVocabbed = this.getVocabbedEntity(
          vocabularyTerms,
          restriction.entityType,
          restriction.entityNames.length > 1,
        )
      }
    }
  }

  private addPermissionToResultMap(
    permission: UserGroupPermission,
    resultMap: PermissionIdToPermissionMap,
    restrictedEntityNames?: RestrictedEntityLabelsByType,
  ): void {
    if (restrictedEntityNames) {
      for (const entity of permission.models?.filter(m => m.restrictedModelId !== null) || []) {
        const entityTypeIsEnabled = this.enabledRestrictions.includes(entity.restrictedModelType)

        if (entityTypeIsEnabled) {
          const restriction = this.getPermissionRestrictionContainer(resultMap, permission, entity)
          const entityName = this.findEntityName(restrictedEntityNames, entity)

          // entityName will be undefined for archived entities
          if (!entityName) {
            continue
          }

          restriction.entityNames.push(entityName)
        }
      }
    }
  }

  private findEntityName(restrictedEntityNames: RestrictedEntityLabelsByType, entity: RestrictedModel): string {
    return restrictedEntityNames[entity.restrictedModelType][entity.restrictedModelId!]
  }

  private getPermissionRestrictionContainer(
    resultMap: PermissionIdToPermissionMap,
    permission: UserGroupPermission,
    entity: RestrictedModel,
  ): Restriction {
    let entityTypeContainer: Restriction | undefined = resultMap[permission.id].restrictedBy.find(
      a => entity.restrictedModelType === a.entityType,
    )

    if (entityTypeContainer === undefined) {
      entityTypeContainer = this.createInitialRestrictedByObject(entity)

      resultMap[permission.id].restrictedBy.push(entityTypeContainer)
    }

    return entityTypeContainer
  }

  private deduplicateViewModelResultMap(resultMap: PermissionIdToPermissionMap): void {
    for (const permissionId of Object.keys(resultMap)) {
      for (const restriction of resultMap[permissionId].restrictedBy) {
        restriction.entityNames = removeDuplicatesFromArray(restriction.entityNames)
      }
    }
  }

  private createInitialRestrictedByObject(model: RestrictedModel): Restriction {
    return {
      entityType: model.restrictedModelType,
      entityTypeVocabbed: model.restrictedModelType,
      entityNames: [],
    }
  }

  private getVocabbedEntity(terms: VocabularyTerms, entityType: EntityType, plural: boolean): string {
    // locations are vocabbed as courtroom
    const stringToCheck = entityType === 'location' ? 'courtroom' : entityType
    const hasVocab = Object.keys(terms).includes(stringToCheck)
    if (hasVocab) {
      const term = terms[stringToCheck as keyof VocabularyTerms]
      return plural ? term.plural : term.singular
    }
    return plural ? stringToCheck + 's' : stringToCheck
  }

  trackBy: TrackByFunction<Permission> = (_idx: number, item: Permission) => {
    return item.id
  }

  private ensureResultMapPermissionEntryExists(
    resultMap: PermissionIdToPermissionMap,
    permission: UserGroupPermission,
  ): void {
    if (resultMap[permission.id] === undefined) {
      resultMap[permission.id] = {
        id: permission.id,
        description: getPermissionDescription(permission.id) || permission.id,
        restrictedBy: [],
        isExpanded: false,
      }
    }
  }

  /**
   * Builds location and department labels if court system has enabled restrictions.
   */
  private getRestrictedLabels(): ApiResult<RestrictedEntityLabelsByType | undefined> {
    const restrictionsEnabled = !!(this.enabledRestrictions?.length > 0)
    if (restrictionsEnabled) {
      return ApiResult.success({
        location: buildLocationNames(this.rootLocations),
        department: buildDepartmentNames(this.departments),
      })
    } else {
      return ApiResult.success(undefined)
    }
  }

  private getUserGroupsWithPermissions(): ApiResult<UserGroup[]> {
    return ApiResult.combine(this.userGroups.map(ug => this.userGroupService.get(ug.id)))
  }
}

export function buildLocationNames(locations: RootLocation[]): EntityIdWithName {
  const children = locations.flatMap(l => l.children)

  return children.reduce((restrictedEntities, childLocation) => {
    restrictedEntities[childLocation.id] = `${childLocation.parent.name} - ${childLocation.name}`
    return restrictedEntities
  }, {} as EntityIdWithName)
}

export function buildDepartmentNames(departments: Department[]): EntityIdWithName {
  return departments.reduce((restrictedEntities, department) => {
    restrictedEntities[department.id] = department.name
    return restrictedEntities
  }, {} as EntityIdWithName)
}

function removeDuplicatesFromArray(array: string[]): string[] {
  return [...new Set(array)]
}
